import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, UserCheck, UserX, Search, 
  Filter, MoreVertical, Mail, Phone, Calendar,
  DollarSign, Edit2, Ban, CheckCircle, XCircle
} from 'lucide-react';
import { agentsService, agencyService } from '../../services';
import './AgentManagement.css';

const AgentManagement = () => {
  const [activeTab, setActiveTab] = useState('agents');
  const [agents, setAgents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalRevenue: 0,
    avgCommissionRate: 0
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'agents') {
        // Use agency endpoint for agency-wide agent view
        const data = await agencyService.getAgents();
        setAgents(data.agents || []);
        // Update stats if provided
        if (data.stats) {
          setStats({
            totalAgents: data.stats.totalAgents || 0,
            activeAgents: data.stats.activeAgents || 0,
            totalRevenue: data.stats.totalRevenue || 0,
            avgCommissionRate: data.stats.avgCommissionRate || 0
          });
        }
      } else {
        // Use agency endpoint for applications
        const data = await agencyService.getApplications();
        setApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveApplication = async (applicationId) => {
    try {
      const result = await agentsService.approveApplication(applicationId);
      alert(`Application approved! Temporary password: ${result.tempPassword}`);
      loadData();
      setShowApplicationModal(false);
    } catch (error) {
      console.error('Failed to approve application:', error);
      alert('Failed to approve application');
    }
  };

  const handleDenyApplication = async (applicationId, reason) => {
    try {
      await agentsService.denyApplication(applicationId, reason);
      loadData();
      setShowApplicationModal(false);
    } catch (error) {
      console.error('Failed to deny application:', error);
      alert('Failed to deny application');
    }
  };

  const handleUpdateAgentStatus = async (agentId, status) => {
    try {
      await agentsService.updateStatus(agentId, status);
      loadData();
    } catch (error) {
      console.error('Failed to update agent status:', error);
      alert('Failed to update agent status');
    }
  };

  const handleUpdateCommission = async (agentId, percentage) => {
    try {
      await agentsService.updateCommission(agentId, percentage);
      loadData();
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update commission:', error);
      alert('Failed to update commission');
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredApplications = applications.filter(app =>
    `${app.first_name} ${app.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Agent Management</h1>
        <p className="text-gray-600 mt-2">Manage your agency's agents and review applications</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAgents}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Agents</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeAgents}</p>
            </div>
            <UserCheck className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Agency Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Commission</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avgCommissionRate}%</p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'agents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline-block mr-2" />
              Active Agents ({agents.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'applications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus className="w-4 h-4 inline-block mr-2" />
              Applications ({applications.length})
            </button>
          </nav>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'agents' ? 'agents' : 'applications'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loading-spinner"></div>
            </div>
          ) : activeTab === 'agents' ? (
            // Agents Table
            <div className="overflow-x-auto">
              {filteredAgents.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No agents found</p>
              ) : (
                <table className="min-w-full admin-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAgents.map((agent) => (
                      <tr key={agent.id}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                            <p className="text-sm text-gray-500">{agent.agency_name}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="text-gray-900 flex items-center">
                              <Mail className="w-4 h-4 mr-1 text-gray-400" />
                              {agent.email}
                            </p>
                            {agent.phone && (
                              <p className="text-gray-500 flex items-center mt-1">
                                <Phone className="w-4 h-4 mr-1 text-gray-400" />
                                {agent.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {agent.commission_percentage}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            agent.status === 'active' 
                              ? 'bg-green-100 text-green-800'
                              : agent.status === 'inactive'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {agent.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(agent.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAgent(agent);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {agent.status === 'active' ? (
                              <button
                                onClick={() => handleUpdateAgentStatus(agent.id, 'inactive')}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateAgentStatus(agent.id, 'active')}
                                className="text-green-600 hover:text-green-800"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            // Applications List
            <div className="space-y-4">
              {filteredApplications.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No pending applications</p>
              ) : (
                filteredApplications.map((application) => (
                  <div key={application.id} className="bg-gray-50 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.first_name} {application.last_name}
                        </h3>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600 flex items-center">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {application.email}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {application.phone}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            Applied {new Date(application.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                        {application.address && (
                          <p className="text-sm text-gray-500 mt-2">
                            {application.address}, {application.city}, {application.state} {application.zip_code}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowApplicationModal(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Review
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Agent Modal */}
      {showEditModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Agent Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Percentage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  defaultValue={selectedAgent.commission_percentage}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="commission-input"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const percentage = document.getElementById('commission-input').value;
                  handleUpdateCommission(selectedAgent.id, percentage);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Review Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Review Application</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Personal Information</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium">Name:</span> {selectedApplication.first_name} {selectedApplication.last_name}</p>
                    <p className="text-sm"><span className="font-medium">Email:</span> {selectedApplication.email}</p>
                    <p className="text-sm"><span className="font-medium">Phone:</span> {selectedApplication.phone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Address</h3>
                  <div className="space-y-2">
                    <p className="text-sm">{selectedApplication.address}</p>
                    <p className="text-sm">{selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}</p>
                    <p className="text-sm"><span className="font-medium">Country:</span> {selectedApplication.country}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Tax Form Requirement</h3>
                <p className="text-sm">
                  This agent will need to complete a <span className="font-medium">{selectedApplication.tax_form_type}</span> form after approval.
                </p>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowApplicationModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const reason = prompt('Reason for denial (optional):');
                  handleDenyApplication(selectedApplication.id, reason);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Deny
              </button>
              <button
                onClick={() => handleApproveApplication(selectedApplication.id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagement;
